import React from "react";
import { fieldText, isOn, type ContratFields } from "types/contrat_location";

/**
 * On-screen replica of the generated contrat de location/prêt: every value the PDF prints
 * is an input here, so the user can type whatever they need before generating. The CSS
 * below mirrors `heric-back/app/templates/contrat_location.html` (same pt sizes, same
 * boxes) — keep the two in sync when either changes.
 *
 * Fields are rendered by plain helper functions (not inner components) on purpose: an
 * inner component would be a new type on every keystroke and the focused input would be
 * remounted.
 */

/** A4 width at 96dpi — the sheet is scaled down to fit its container. */
export const SHEET_WIDTH = 794;

const SHEET_CSS = `
.cl-sheet {
  width: ${SHEET_WIDTH}px;
  padding: 30px;
  background: #fff;
  font-family: 'Helvetica', 'Arial', sans-serif;
  font-size: 8.5pt;
  line-height: 1.35;
  color: #1a1a1a;
  box-sizing: border-box;
}
.cl-sheet * { margin: 0; padding: 0; box-sizing: border-box; }
.cl-sheet .header-row { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 6px; }
.cl-sheet .header-left h1 { font-size: 16pt; color: #1e3a5f; margin-bottom: 2px; }
.cl-sheet .stamp-box { border: 1px solid #ccc; border-radius: 4px; padding: 5px 8px; }
.cl-sheet .stamp-box .stamp-title { font-size: 7pt; text-transform: uppercase; color: #888; text-align: center; margin-bottom: 3px; }
.cl-sheet .stamp-box .org-name { font-size: 10pt; font-weight: bold; color: #1e3a5f; text-align: center; }
.cl-sheet .stamp-box .org-detail { font-size: 7.5pt; color: #555; text-align: center; }
.cl-sheet .contract-meta { display: flex; align-items: center; gap: 20px; margin-bottom: 6px; }
.cl-sheet .contract-type { display: flex; gap: 10px; font-size: 9pt; }
.cl-sheet .contract-type span { padding: 2px 10px; border: 1.5px solid #1e3a5f; border-radius: 3px; color: #1e3a5f; font-weight: bold; }
.cl-sheet .contract-type .active { background: #1e3a5f; color: white; }
.cl-sheet .contract-num { font-size: 10pt; font-weight: bold; color: #1e3a5f; display: flex; align-items: baseline; gap: 4px; }
.cl-sheet .main-body { display: grid; grid-template-columns: 48% 52%; gap: 6px; }
.cl-sheet .box { border: 1px solid #bbb; border-radius: 3px; padding: 5px 7px; margin-bottom: 5px; }
.cl-sheet .box-title, .cl-sheet .box-title-red {
  font-size: 7.5pt; font-weight: bold; text-transform: uppercase; letter-spacing: 0.4px;
  color: white; background: #1e3a5f; padding: 2px 7px; border-radius: 2px;
  margin: -5px -7px 5px -7px; display: inline-block;
}
.cl-sheet .box-title-red { background: #b91c1c; }
.cl-sheet .fr { display: flex; align-items: baseline; margin-bottom: 2px; }
.cl-sheet .fl { font-size: 7.5pt; color: #444; min-width: 90px; flex-shrink: 0; }
.cl-sheet .fl-w { min-width: 110px; }
.cl-sheet .fl-s { min-width: 65px; }
.cl-sheet .ftable { width: 100%; border-collapse: collapse; font-size: 7.5pt; }
.cl-sheet .ftable td { padding: 2px 5px; border-bottom: 1px solid #e5e5e5; }
.cl-sheet .ftable tr:nth-child(even) td { background: #f8f9fa; }
.cl-sheet .opt-row { display: flex; align-items: center; gap: 6px; padding: 2px 0; border-bottom: 1px solid #eee; font-size: 7.5pt; }
.cl-sheet .opt-label { font-weight: bold; min-width: 120px; }
.cl-sheet .opt-choice { display: flex; align-items: center; gap: 3px; cursor: pointer; }
.cl-sheet .total-row { display: flex; justify-content: space-between; align-items: baseline; padding: 1px 0; font-size: 7.5pt; gap: 8px; }
.cl-sheet .total-row.bold { font-weight: bold; font-size: 9pt; color: #1e3a5f; }
.cl-sheet .total-row.main { font-weight: bold; font-size: 10pt; color: #1e3a5f; border-top: 2px solid #1e3a5f; padding-top: 3px; margin-top: 2px; }
.cl-sheet .cgl-box { display: flex; align-items: center; gap: 8px; border: 1px solid #1e3a5f; border-radius: 3px; padding: 4px 6px; margin-top: 4px; background: #f8fafc; }
.cl-sheet .cgl-box img { width: 52px; height: 52px; flex-shrink: 0; }
.cl-sheet .cgl-qr-ph { width: 52px; height: 52px; flex-shrink: 0; border: 1px dashed #94a3b8; border-radius: 2px; }
.cl-sheet .cgl-text { font-size: 6.8pt; color: #333; line-height: 1.3; }
.cl-sheet .cgl-title { font-size: 7.5pt; font-weight: bold; color: #1e3a5f; }
.cl-sheet .cgl-url { font-size: 6.5pt; color: #1e3a5f; word-break: break-all; }
.cl-sheet .cgl-accept { font-size: 7.2pt; font-weight: bold; margin-top: 2px; display: flex; align-items: center; gap: 3px; cursor: pointer; }
.cl-sheet .legal { font-size: 5.6pt; color: #666; line-height: 1.22; margin-top: 4px; text-align: justify; }
.cl-sheet .sig-lbl { font-size: 7pt; color: #555; }
.cl-sheet .fuel-row { display: flex; gap: 5px; align-items: center; font-size: 7.5pt; margin-top: 4px; flex-wrap: wrap; }
.cl-sheet .cb-label { display: flex; align-items: center; gap: 2px; cursor: pointer; }
.cl-sheet .footer { margin-top: 5px; padding-top: 3px; border-top: 1px solid #e2e8f0; font-size: 7pt; color: #94a3b8; text-align: center; }

/* ── editable fields ── */
.cl-sheet .cl-inp {
  font-family: inherit; font-size: 8.5pt; color: inherit;
  border: 0; border-bottom: 1px dotted #aaa; outline: 0; background: transparent;
  padding: 0 3px; min-height: 14px; width: 100%; min-width: 0; flex: 1;
  border-radius: 2px; transition: background-color .12s, box-shadow .12s;
}
.cl-sheet .cl-inp.filled { background: #f0f6ff; border-bottom: 1px solid #ccc; font-weight: 500; }
.cl-sheet .cl-inp:hover { background: #eef2ff; }
.cl-sheet .cl-inp:focus { background: #fff; box-shadow: 0 0 0 2px #6366f1; border-bottom-color: transparent; }
.cl-sheet .cl-inp::placeholder { color: #cbd5e1; font-style: italic; }
.cl-sheet .cl-inp.missing { background: #fef2f2; border-bottom: 1.5px solid #b91c1c; }
.cl-sheet .cl-inp.missing::placeholder { color: #b91c1c; font-style: normal; }
.cl-sheet .cl-inp.right { text-align: right; }
.cl-sheet .cl-inp.small { font-size: 7.5pt; }
.cl-sheet .cl-inp.big { font-size: 10pt; font-weight: bold; color: #1e3a5f; }
.cl-sheet textarea.cl-inp { resize: vertical; min-height: 34px; line-height: 1.3; font-size: 7.5pt; }
.cl-sheet .cl-cb {
  width: 11px; height: 11px; border: 1.5px solid #666; border-radius: 2px;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 8pt; font-weight: bold; color: #1e3a5f; line-height: 1;
  cursor: pointer; flex-shrink: 0; background: #fff;
}
.cl-sheet .cl-cb.on { border-color: #1e3a5f; background: #dbeafe; }
.cl-sheet .cl-cb:hover { border-color: #6366f1; }
.cl-sheet .sig-pad { height: 42px; border: 1px dotted #aaa; border-radius: 2px; }
`;

export interface ContratSheetProps {
  fields: ContratFields;
  onChange: (key: string, value: string | boolean) => void;
  usage: "location" | "pret";
  org: Record<string, string>;
  conditionsUrl: string;
  conditionsQr?: string;
  generatedAt?: string;
  /** Mandatory fields still empty — flagged in red on the sheet. */
  missingKeys?: string[];
}

export default function ContratLocationSheet({
  fields,
  onChange,
  usage,
  org,
  conditionsUrl,
  conditionsQr,
  generatedAt,
  missingKeys,
}: ContratSheetProps) {
  const usageWord = usage === "location" ? "location" : "prêt";

  /** One editable value of the contract. */
  const inp = (
    key: string,
    opts?: { className?: string; style?: React.CSSProperties; placeholder?: string },
  ) => {
    const value = fieldText(fields, key);
    const missing = !value && missingKeys?.includes(key);
    return (
      <input
        className={`cl-inp${value ? " filled" : ""}${missing ? " missing" : ""}${opts?.className ? ` ${opts.className}` : ""}`}
        style={opts?.style}
        value={value}
        placeholder={missing ? "Obligatoire" : opts?.placeholder}
        onChange={(e) => onChange(key, e.target.value)}
      />
    );
  };

  const cb = (on: boolean, toggle: () => void) => (
    <span
      className={`cl-cb${on ? " on" : ""}`}
      role="checkbox"
      aria-checked={on}
      tabIndex={0}
      onClick={toggle}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          toggle();
        }
      }}
    >
      {on ? "✓" : ""}
    </span>
  );

  /** Radio-like choice: clicking the selected value clears it. */
  const pick = (key: string, value: string) =>
    onChange(key, fieldText(fields, key) === value ? "" : value);

  const optRow = (label: string, key: string) => (
    <div className="opt-row">
      <span style={{ flex: 1 }}>{label}</span>
      <span className="opt-choice" onClick={() => pick(key, "oui")}>
        {cb(fieldText(fields, key) === "oui", () => pick(key, "oui"))} Oui
      </span>
      <span className="opt-choice" onClick={() => pick(key, "non")}>
        {cb(fieldText(fields, key) === "non", () => pick(key, "non"))} Non
      </span>
    </div>
  );

  const amount = { maxWidth: 80, flex: "0 0 80px" } as React.CSSProperties;

  const totalRow = (label: string, key: string, cls = "") => (
    <div className={`total-row${cls ? ` ${cls}` : ""}`}>
      <span>{label}</span>
      {inp(key, { className: "right", style: amount })}
    </div>
  );

  const acceptConditions = () => onChange("conditions_accepted", !isOn(fields, "conditions_accepted"));

  return (
    <>
      <style>{SHEET_CSS}</style>
      <div className="cl-sheet">
        {/* ═══ HEADER ═══ */}
        <div className="header-row">
          <div className="header-left">
            <h1>Contrat de {usage === "location" ? "Location" : "Prêt"}</h1>
          </div>
          <div className="stamp-box">
            <div className="stamp-title">Tampon de l'établissement</div>
            <div className="org-name">{org.name}</div>
            {org.address && <div className="org-detail">{org.address}</div>}
            {(org.postal || org.city) && (
              <div className="org-detail">{`${org.postal || ""} ${org.city || ""}`.trim()}</div>
            )}
            {org.phone_number && <div className="org-detail">Tél : {org.phone_number}</div>}
            {org.siret && <div className="org-detail">SIRET : {org.siret}</div>}
          </div>
        </div>

        <div className="contract-meta">
          <div className="contract-type">
            <span className="active">CONTRAT</span>
            <span>FACTURE</span>
          </div>
          <div className="contract-num">N° R {inp("contract_num", { className: "big", style: { maxWidth: 150 } })}</div>
        </div>

        {/* ═══ MAIN TWO-COLUMN BODY ═══ */}
        <div className="main-body">
          {/* ════ LEFT COLUMN ════ */}
          <div>
            <div className="box">
              <div className="box-title">Véhicule</div>
              <div className="fr"><span className="fl">Catégorie :</span>{inp("veh_categorie")}</div>
              <div className="fr"><span className="fl">Marque :</span>{inp("veh_marque")}</div>
              <div className="fr"><span className="fl">Modèle :</span>{inp("veh_modele")}</div>
              <div className="fr"><span className="fl">Immatriculation :</span>{inp("veh_immat")}</div>
            </div>

            <div className="box">
              <div className="box-title">Conducteur principal</div>
              <div className="fr"><span className="fl">Nom :</span>{inp("cp_nom")}</div>
              <div className="fr"><span className="fl">Adresse :</span>{inp("cp_adresse")}</div>
              <div className="fr" style={{ marginLeft: 90 }}>{inp("cp_adresse2")}</div>
              <div className="fr"><span className="fl">Tél :</span>{inp("cp_tel")}</div>
              <div className="fr"><span className="fl fl-w">Date et lieu de naissance :</span>{inp("cp_naissance")}</div>
              <div className="fr">
                <span className="fl">N° permis :</span>{inp("cp_permis")}
                <span className="fl fl-s" style={{ marginLeft: 8 }}>Délivré le :</span>
                {inp("cp_permis_le", { style: { flex: 0.6 } })}
              </div>
              <div className="fr"><span className="fl">Par :</span>{inp("cp_permis_par")}</div>
            </div>

            <div className="box">
              <div className="box-title">Conducteur(s) additionnel(s)</div>
              <div className="fr"><span className="fl">Nom :</span>{inp("ca_nom")}</div>
              <div className="fr"><span className="fl">Adresse :</span>{inp("ca_adresse")}</div>
              <div className="fr" style={{ marginLeft: 90 }}>{inp("ca_adresse2")}</div>
              <div className="fr"><span className="fl">Tél :</span>{inp("ca_tel")}</div>
              <div className="fr"><span className="fl fl-w">Date et lieu de naissance :</span>{inp("ca_naissance")}</div>
              <div className="fr">
                <span className="fl">N° permis :</span>{inp("ca_permis")}
                <span className="fl fl-s" style={{ marginLeft: 8 }}>Délivré le :</span>
                {inp("ca_permis_le", { style: { flex: 0.6 } })}
              </div>
              <div className="fr"><span className="fl">Par :</span>{inp("ca_permis_par")}</div>
            </div>

            <div className="box" style={{ marginTop: 2 }}>
              <div style={{ fontSize: "7pt", fontWeight: "bold", color: "#888", textAlign: "center", marginBottom: 3 }}>
                COÛT / JOUR
              </div>
              <div className="opt-row" style={{ borderBottom: "1px solid #ddd" }}>
                <span className="opt-label">FRANCHISE ACCIDENT :</span>
                {inp("franchise_accident")}
              </div>
              <div className="opt-row">
                <span className="opt-label">FRANCHISE VOL :</span>
                {inp("franchise_vol")}
              </div>
            </div>

            <div className="box">
              <div className="box-title">Dépôt de garantie</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2px 12px" }}>
                <div className="fr"><span className="fl fl-s">Date :</span>{inp("depot_date")}</div>
                <div className="fr"><span className="fl fl-s">Montant :</span>{inp("depot_montant")}</div>
                <div className="fr"><span className="fl fl-s">Mode :</span>{inp("depot_mode")}</div>
                <div className="fr"><span className="fl fl-s">N° :</span>{inp("depot_num")}</div>
                <div className="fr"><span className="fl fl-s">Banque :</span>{inp("depot_banque")}</div>
                <div className="fr"><span className="fl fl-s">Autorisation :</span>{inp("depot_autorisation")}</div>
              </div>
            </div>

            {/* Conditions générales : QR + acceptation */}
            <div className="cgl-box">
              {conditionsQr ? <img src={conditionsQr} alt="QR Conditions générales" /> : <div className="cgl-qr-ph" />}
              <div className="cgl-text">
                <div className="cgl-title">Conditions générales de {usageWord}</div>
                <div>Scannez le QR code pour les consulter et les conserver :</div>
                <div className="cgl-url">{conditionsUrl}</div>
                <div className="cgl-accept" onClick={acceptConditions}>
                  {cb(isOn(fields, "conditions_accepted"), acceptConditions)}
                  J'accepte les conditions générales de {usageWord}
                </div>
              </div>
            </div>

            <div className="legal">
              Je déclare avoir pris connaissance des Conditions générales de location visées aux présentes et en accepter les termes sans
              exception ni réserve. Si la location est réglée au moyen de la carte bancaire mentionnée aux présentes ou doit être facturée
              sur une carte accréditive, la signature ci-dessous sera considérée comme ayant été faite sur la note de débit de la carte
              concernée. En cas de paiement par carte bancaire ou par carte accréditive, j'autorise que tous les suppléments, y compris la
              facturation des dommages en cas d'Accident, les Contraventions, les coûts de traitement administratif, pénalités, etc., soient
              facturés et prélevés sur ma carte. Conditions de paiement : Comptant. En cas de retard de paiement, des pénalités de retard
              égales à trois fois le taux d'intérêt légal ainsi qu'une indemnité forfaitaire de 40 Euros pour frais de recouvrement seront
              appliquées conformément aux articles L 441-3 et L 441-6 du Code de Commerce. En signant le Contrat de location, j'autorise
              l'utilisation de mes données personnelles dans les conditions prévues aux présentes.
            </div>
          </div>

          {/* ════ RIGHT COLUMN ════ */}
          <div>
            <div className="box">
              <div className="box-title">Locataire / Conducteur</div>
              <div className="fr">{inp("loc_nom", { style: { fontWeight: 500 } })}</div>
              <div className="fr"><span className="fl fl-s">Tél :</span>{inp("loc_tel", { className: "small" })}</div>
              <div className="fr"><span className="fl fl-s">Email :</span>{inp("loc_email", { className: "small" })}</div>
            </div>

            <div className="box">
              <table style={{ width: "100%", fontSize: "7.5pt", borderCollapse: "collapse" }}>
                <tbody>
                  <tr>
                    <td style={{ padding: "2px 4px" }}>
                      <div className="fr"><span className="fl fl-s">Lieu :</span>{inp("lieu_depart", { className: "small" })}</div>
                    </td>
                    <td style={{ padding: "2px 4px" }}>
                      <div className="fr"><span className="fl fl-s">Date départ :</span>{inp("date_depart", { className: "small" })}</div>
                    </td>
                    <td style={{ padding: "2px 4px" }}>
                      <div className="fr"><span className="fl fl-s">Heure :</span>{inp("heure_depart", { className: "small" })}</div>
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: "2px 4px" }}>
                      <div className="fr"><span className="fl fl-s">Lieu :</span>{inp("lieu_retour", { className: "small" })}</div>
                    </td>
                    <td style={{ padding: "2px 4px" }}>
                      <div className="fr"><span className="fl fl-s">Retour prévu :</span>{inp("date_retour", { className: "small" })}</div>
                    </td>
                    <td style={{ padding: "2px 4px" }}>
                      <div className="fr"><span className="fl fl-s">Heure :</span>{inp("heure_retour", { className: "small" })}</div>
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: "2px 4px" }}>
                      <div className="fr"><span className="fl fl-s">Lieu :</span>{inp("lieu_reel", { className: "small" })}</div>
                    </td>
                    <td style={{ padding: "2px 4px" }}>
                      <div className="fr"><span className="fl fl-s">Date réelle :</span>{inp("date_reelle", { className: "small" })}</div>
                    </td>
                    <td style={{ padding: "2px 4px" }}>
                      <div className="fr"><span className="fl fl-s">Heure :</span>{inp("heure_reelle", { className: "small" })}</div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="box">
              <div className="box-title">Forfait TTC</div>
              <table className="ftable">
                <tbody>
                  <tr>
                    <td>Km Retour</td>
                    <td style={{ width: 70 }}>{inp("km_retour", { className: "small right" })}</td>
                    <td style={{ borderLeft: "1px solid #ddd" }}>Heures à</td>
                    <td style={{ width: 70 }}>{inp("heures_a", { className: "small right" })}</td>
                  </tr>
                  <tr>
                    <td>Km Départ</td>
                    <td>{inp("km_depart", { className: "small right" })}</td>
                    <td style={{ borderLeft: "1px solid #ddd" }}>Jours à</td>
                    <td>{inp("jours_a", { className: "small right" })}</td>
                  </tr>
                  <tr>
                    <td>Km Parcourus</td>
                    <td>{inp("km_parcourus", { className: "small right" })}</td>
                    <td style={{ borderLeft: "1px solid #ddd" }}>Mois à</td>
                    <td>{inp("mois_a", { className: "small right" })}</td>
                  </tr>
                  <tr>
                    <td>Km Alloués</td>
                    <td>{inp("km_alloues", { className: "small right" })}</td>
                    <td style={{ borderLeft: "1px solid #ddd" }}>Km sup. à</td>
                    <td>{inp("km_sup_a", { className: "small right" })}</td>
                  </tr>
                  <tr>
                    <td>Km Facturés</td>
                    <td>{inp("km_factures", { className: "small right" })}</td>
                    <td style={{ borderLeft: "1px solid #ddd" }} />
                    <td />
                  </tr>
                </tbody>
              </table>
            </div>

            <div style={{ padding: "0 2px" }}>
              {totalRow("TOTAL LOCATION HT", "total_location_ht", "bold")}
              {totalRow("Livraison/reprise HT", "livraison_ht")}
              {totalRow("TOTAL HT", "total_ht", "bold")}
            </div>

            <div className="box" style={{ marginTop: 4 }}>
              <div className="box-title">Options facturées HT</div>
              {optRow("Réduction Franchise Accident :", "opt_accident")}
              {optRow("Réduction Franchise Vol :", "opt_vol")}
              {optRow("Assistance technique :", "opt_assistance")}
            </div>

            <div style={{ padding: "0 2px", marginTop: 4 }}>
              {totalRow("Accessoires + Ou - Divers HT", "accessoires_ht")}
              {totalRow("TOTAL HT", "total_ht_2", "bold")}
              {totalRow("TVA %", "tva_pct")}
              <div className="total-row bold" style={{ borderTop: "1px solid #1e3a5f", paddingTop: 3, marginTop: 2 }}>
                <span>TOTAL LOCATION TTC</span>
                {inp("total_ttc", { className: "right", style: amount })}
              </div>
              {totalRow("Carburant", "carburant_montant")}
              <div className="total-row main">
                <span>TOTAL DÛ TTC</span>
                {inp("total_du_ttc", { className: "right", style: amount })}
              </div>
            </div>

            <div className="fuel-row">
              <span style={{ fontWeight: "bold" }}>Carburant :</span>
              {["GO", "SP"].map((t) => (
                <span key={t} className="cb-label" onClick={() => pick("carburant_type", t)}>
                  {cb(fieldText(fields, "carburant_type") === t, () => pick("carburant_type", t))} {t}
                </span>
              ))}
              <span style={{ width: 8 }} />
              {["V", "1/4", "1/2", "3/4", "P"].map((n) => (
                <span key={n} className="cb-label" onClick={() => pick("carburant_niveau", n)}>
                  {cb(fieldText(fields, "carburant_niveau") === n, () => pick("carburant_niveau", n))} {n}
                </span>
              ))}
            </div>
            <div className="fr" style={{ marginTop: 3 }}>
              <span className="fl fl-s">Préparé par :</span>{inp("prepared_by")}
            </div>

            <div className="box" style={{ marginTop: 5 }}>
              <div className="box-title">Observations</div>
              <textarea
                className={`cl-inp${fieldText(fields, "observations") ? " filled" : ""}`}
                value={fieldText(fields, "observations")}
                placeholder="Texte libre : état du véhicule, accord particulier, consignes…"
                onChange={(e) => onChange("observations", e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* ═══ Véhicule en réparation + signatures ═══ */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 4 }}>
          <div>
            <div className="box" style={{ background: "#fafafa", marginBottom: 4 }}>
              <div className="box-title-red">
                Véhicule en réparation (motif du {usage === "location" ? "remplacement" : "prêt"})
              </div>
              <div className="fr"><span className="fl fl-s">Véhicule :</span>{inp("rep_vehicule")}</div>
              <div className="fr"><span className="fl fl-s">Immat. :</span>{inp("rep_immat")}</div>
              <div className="fr"><span className="fl fl-s">Ordre :</span>{inp("rep_ordre")}</div>
            </div>
            <div style={{ display: "flex", gap: 12 }}>
              <div className="fr" style={{ flex: 1 }}>
                <span className="fl fl-s sig-lbl">Règlement date :</span>{inp("reglement_date", { className: "small" })}
              </div>
              <div className="fr" style={{ flex: 1 }}>
                <span className="fl fl-s sig-lbl">Mode :</span>{inp("reglement_mode", { className: "small" })}
              </div>
            </div>
          </div>
          <div>
            <div style={{ fontSize: "7.5pt", fontWeight: "bold" }}>"Bon pour {usageWord}"</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 2 }}>
              <div>
                <span className="sig-lbl">Le client :</span>
                <div className="sig-pad" />
              </div>
              <div>
                <span className="sig-lbl">L'établissement :</span>
                <div className="sig-pad" />
              </div>
            </div>
          </div>
        </div>

        <div className="footer">
          Contrat généré le {generatedAt || "…"} — {org.name}
        </div>
      </div>
    </>
  );
}
